/**
 * @file AssetLibrary.js
 * @description This page displays the assets with their respective details such as last name, first name, username, scope... and 3 different view modes (list, card list, and grid).
 */

import '../page-list/PageList.css';
import styles from './AssetLibrary.module.scss';
import classNames from 'classnames';
import { useState, useEffect, useRef, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Toast from '../ui/toasts/Toast';
import Spinner from '../ui/spinners/Spinner';
import { ModalContext, ModalTypes } from '../../store/ModalContext';
import {
    deleteAsset,
    fetchAssets,
    removeAsset,
    getClientOptions,
} from '../../services/clientService';
import { Dropdown } from '../helpers/Dropdown';
import { SearchableDropdown } from '../helpers/SearchableDropdown';
import NotFoundBox from '../ui/no_data_indicators/NotFoundBox';
import AssetDrawer from './AssetDrawer';
import PaginationComponent from '../helpers/PaginationComponent';
import AssetGridView from './components/AssetGridView';
import AssetListView from './components/AssetListView';
import FullscreenDisplay from './shared/FullscreenDisplay';
import {
    getFileCategory,
    canRenderAssetPreviewInBrowser,
} from '../helpers/FileUtils';
import { useAuthContext } from '../../store/Contexts';
import {
    EventDataTypeEnum,
    ClientOptionEnum,
    LogEventType,
    ResourceProgressStatusEnum,
    ResourceScope,
    SecurityAttributes,
    UserType,
} from '../../utils/data/enums';
import { logEvent } from '../../services/loggingService';
import { getText } from '../../i18n';
import {
    getResourceScopesForUser,
    normalizeUserType,
} from './resourceScopeUtils';

const AssetLibrary = ({ viewMode }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const tenantId = Number(window.localStorage.getItem('tenantId')) || null;
    const [assets, setAssets] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState([]);
    const [sortParam, setSortParam] = useState(getText('msg_3ae085e5fa14'));
    const [selectedViewMode, setSelectedViewMode] = useState('grid');
    const [activeCard, setActiveCard] = useState(null);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [totalPages, setTotalPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const { showModal } = useContext(ModalContext);
    const clickTimer = useRef(null);
    const cardRef = useRef(null);
    const iconRef = useRef(null);
    const isMyAssets = viewMode === 'MyAssets';
    const isGlobalAssets = viewMode === 'GlobalAssets';
    const isMultiTenantTrainingView = viewMode === 'MultiTenantTraining';
    const {
        user,
        hasSecurityAttribute,
        menuLabels,
        menuLabelsLoaded,
        adminView,
        multitenantFlag,
    } = useAuthContext();
    const isAssetLibraryReadOnly = hasSecurityAttribute([
        SecurityAttributes.AssetLibraryReadOnly,
    ]);

    const [isMobile, setIsMobile] = useState(window.innerWidth <= 786);
    const assetsMenuLabel = menuLabelsLoaded ? menuLabels.assets : '';
    const [allowAssetUpload, setAllowAssetUpload] = useState(false);
    const isResourcePage =
        viewMode === 'ResourceLibrary' || isMultiTenantTrainingView;
    const canManageResources = hasSecurityAttribute([
        SecurityAttributes.ResourceLibraryFullAccess,
    ]);
    const normalizedUserType = normalizeUserType(user?.jwt?.type);
    const canSwitchToIndividualView =
        user?.jwt?.ind &&
        user?.jwt?.tenant == tenantId &&
        !multitenantFlag &&
        normalizedUserType !== null &&
        normalizedUserType <= UserType.ClientAdmin;
    const isHelpMenuIndividualResourceView =
        location.state?.individualResourceScope === true &&
        (canSwitchToIndividualView ||
            normalizedUserType === UserType.Individual);
    const isIndividualResourceView =
        isResourcePage &&
        !multitenantFlag &&
        (isHelpMenuIndividualResourceView ||
            (!adminView &&
                (canSwitchToIndividualView ||
                    normalizedUserType === UserType.Individual)));
    const isAdminResourceView =
        isResourcePage &&
        !multitenantFlag &&
        adminView &&
        !isHelpMenuIndividualResourceView;
    const isClientResourceView =
        isAdminResourceView || isIndividualResourceView;
    const isTrainingConsumptionView =
        isClientResourceView || isMultiTenantTrainingView;
    const isGlobalResourceUser = user?.jwt?.agencies?.includes(0);

    const hasIndividualId = canSwitchToIndividualView;

    const isClientAdminUser =
        normalizedUserType !== null &&
        normalizedUserType <= UserType.ClientAdmin;
    const isIndividualUser = normalizedUserType === UserType.Individual;
    const isTrainingView =
        viewMode === 'MultiTenantTraining' ||
        isAdminResourceView ||
        isIndividualResourceView;
    const shouldForceClientAdminScope =
        isResourcePage &&
        !isMultiTenantTrainingView &&
        !isIndividualUser &&
        (viewMode !== 'ResourceLibrary' || isTrainingView);
    const shouldOmitResourceScopes =
        viewMode === 'ResourceLibrary' && !isTrainingView;

    const resourceScopes = getResourceScopesForUser({
        isMultiTenantTrainingView,
        isGlobalResourceUser,
        isIndividualUser,
        isIndividualResourceView,
        isClientAdminUser,
        hasIndividualId,
        canSwitchToIndividualView,
        shouldForceClientAdminScope,
    });
    const effectiveResourceScopes = resourceScopes;
    const isResourceLibraryReadOnly =
        !canManageResources &&
        hasSecurityAttribute([SecurityAttributes.ResourceLibraryReadOnly]);

    const buildResourceQuery = query => {
        if (!isResourcePage) {
            return query;
        }

        return {
            ...query,
            assetTypes: [6],
            isTrainingView,
            page: currentPage,
            ...(shouldOmitResourceScopes
                ? {}
                : { resourceScopes: effectiveResourceScopes }),
        };
    };

    const [queryParams, setQueryParams] = useState(() => ({
        pageSize: 20,
        ...(isResourcePage
            ? {
                  assetTypes: [6],
                  page: currentPage,
                  ...(shouldOmitResourceScopes
                      ? {}
                      : { resourceScopes: effectiveResourceScopes }),
                  isTrainingView,
              }
            : {}),
        ...(isGlobalAssets ? { isGlobalAsset: true } : {}),
    }));
    const previousViewModeRef = useRef(viewMode);
    const previousResourceScopeRef = useRef(JSON.stringify(resourceScopes));

    useEffect(() => {
        const nextResourceScopeKey = JSON.stringify(resourceScopes);
        const hasViewChanged = previousViewModeRef.current !== viewMode;
        const hasResourceScopeChanged =
            previousResourceScopeRef.current !== nextResourceScopeKey;

        if (!hasViewChanged && !hasResourceScopeChanged) {
            return;
        }

        previousViewModeRef.current = viewMode;
        previousResourceScopeRef.current = nextResourceScopeKey;
        setAssets([]);
        setFilters([]);
        setSearchTerm('');
        setCurrentPage(1);
        setQueryParams({
            pageSize: 20,
            ...(isResourcePage
                ? {
                      assetTypes: [6],
                      page: currentPage,
                      ...(shouldOmitResourceScopes
                          ? {}
                          : { resourceScopes: effectiveResourceScopes }),
                      isTrainingView,
                  }
                : {}),
            ...(isGlobalAssets ? { isGlobalAsset: true } : {}),
        });
    }, [
        viewMode,
        resourceScopes,
        isGlobalAssets,
        isResourcePage,
        isTrainingView,
    ]);

    useEffect(() => {
        const loadAllowAssetUpload = async () => {
            if (!isMyAssets) {
                return;
            }

            try {
                const data = await getClientOptions(0, [
                    ClientOptionEnum.AllowAssetUpload,
                ]);
                const uploadOption = data.find(
                    item =>
                        item.clientOptionId ===
                        ClientOptionEnum.AllowAssetUpload,
                );

                if (uploadOption) {
                    setAllowAssetUpload(uploadOption.value !== 'false');
                }
            } catch (error) {
                console.error(
                    'Failed to fetch allow asset upload option',
                    error,
                );
            }
        };

        loadAllowAssetUpload();
    }, [isMyAssets]);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 786);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const getPageSubtitle = () => {
        if (isResourcePage) {
            if (isAdminResourceView || isMultiTenantTrainingView) {
                return getText('msg_3b0d69c1bd68');
            }

            if (isIndividualResourceView) {
                return getText('msg_09d8acca11af');
            }

            return getText('msg_ffcdcb2e4f87');
        }

        if (isAssetLibraryReadOnly) {
            return getText('msg_a0b7d1f2c3e4');
        }

        let str = getText('msg_b1c2d3e4f506');

        if (isMyAssets) {
            str = getText('msg_c7d8e9f0a1b2');
        } else if (isGlobalAssets) {
            str = getText('msg_d2e3f4a5b6c7');
        }

        return str;
    };

    const filterOptionsAssetLibrary = [
        { id: '5', name: getText('msg_bc17c1f0179a') },
        { id: '6', name: getText('msg_acdac205f5e2') },
        { id: '7', name: getText('msg_50e19fda0d5b') },
        { id: '8', name: getText('msg_e214b8a29923') },
        { id: '9', name: getText('msg_b2c3d4e5f6a7') },
        { id: '1', name: getText('msg_0c359a10ae17') },
        { id: '3', name: getText('msg_c3d4e5f6a7b8') },
        { id: '4', name: getText('msg_50d0d8dd11a9') },
        { id: '2', name: getText('msg_f3a4b5c6d7e8') },
        { id: '10', name: getText('msg_a4b5c6d7e8f9') },
        { id: '13', name: getText('msg_b4c5d6e7f8a9') },
        { id: '14', name: getText('msg_c4d5e6f7a8b9') },
    ];

    const filterOptionsMyAssets = [
        { id: '5', name: getText('msg_bc17c1f0179a') },
        { id: '6', name: getText('msg_acdac205f5e2') },
        { id: '7', name: getText('msg_50e19fda0d5b') },
        { id: '8', name: getText('msg_e214b8a29923') },
        { id: '9', name: getText('msg_b2c3d4e5f6a7') },
        { id: '11', name: getText('msg_d3e4f5a6b7c8') },
        { id: '12', name: getText('msg_e3f4a5b6c7d8') },
        { id: '3', name: getText('msg_c3d4e5f6a7b8') },
    ];

    const filterOptionsGlobalAssets = [
        { id: '5', name: getText('msg_bc17c1f0179a') },
        { id: '6', name: getText('msg_acdac205f5e2') },
        { id: '7', name: getText('msg_50e19fda0d5b') },
        { id: '8', name: getText('msg_e214b8a29923') },
        { id: '9', name: getText('msg_b2c3d4e5f6a7') },
    ];

    const filterOptionsResourceLibrary = [
        { id: '5', name: getText('msg_bc17c1f0179a') },
        { id: '6', name: getText('msg_acdac205f5e2') },
        { id: '7', name: getText('msg_50e19fda0d5b') },
        { id: '8', name: getText('msg_e214b8a29923') },
        { id: '9', name: getText('msg_b2c3d4e5f6a7') },
        ...(isTrainingConsumptionView
            ? [
                  { id: '20', name: getText('msg_db2c45d55793') },
                  { id: '21', name: getText('msg_b6bd42e4e730') },
                  { id: '22', name: getText('msg_1798b3ba42ee') },
              ]
            : [
                  { id: '15', name: getText('msg_b4c5d6e7f8a9') },
                  { id: '16', name: getText('msg_5c47e26c6ad2') },
                  { id: '17', name: getText('msg_24d24ae1e75e') },
                  { id: '18', name: getText('msg_a7abed83ed9e') },
              ]),
    ];

    const getOptions = () => {
        let options = filterOptionsAssetLibrary;

        if (isResourcePage) {
            options = filterOptionsResourceLibrary;
        } else if (isMyAssets) {
            options = filterOptionsMyAssets;
        } else if (isGlobalAssets) {
            options = filterOptionsGlobalAssets;
        }

        return options;
    };

    const sortOptions = [
        { value: 1, label: getText('msg_3ae085e5fa14') },
        { value: 2, label: getText('msg_13105f46e202') },
        { value: 3, label: getText('msg_d43282d36bc3') },
        { value: 4, label: getText('msg_cf1cc03be3d6') },
    ];

    useEffect(() => {
        loadAssets();
    }, []);

    const loadAssets = async () => {
        try {
            setIsLoading(true);
            const resourceQuery = buildResourceQuery(queryParams);
            const fetchedAssets = await fetchAssets(
                resourceQuery,
                null,
                isMyAssets,
            );
            setAssets(fetchedAssets.viewModels);
            setTotalPages(fetchedAssets.pageTotal);
        } catch (error) {
            console.error('Failed to fetch assets', error);
            Toast.error(getText('msg_cade149a85b2'));
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!isLoading) {
            const timeoutId = setTimeout(() => {
                loadAssets();
            }, 300);

            return () => clearTimeout(timeoutId);
        }
    }, [queryParams]);

    const handleSearch = event => {
        setSearchTerm(event.target.value);
    };

    const addOrReplaceQueryParam = (key, value) => {
        setQueryParams(prevParams => ({
            ...prevParams,
            [key]: value,
        }));
    };

    const removeQueryParam = key => {
        setQueryParams(prevParams => {
            const { [key]: _, ...newParams } = prevParams;
            return newParams;
        });
    };

    useEffect(() => {
        const trimmedSearchTerm = searchTerm.trim();

        setCurrentPage(1);
        addOrReplaceQueryParam('page', 1);

        if (trimmedSearchTerm !== '') {
            addOrReplaceQueryParam('search', trimmedSearchTerm);
            return;
        }

        removeQueryParam('search');
    }, [searchTerm]);

    const handleChangeSortBy = value => {
        const item = sortOptions.find(option => option.value === value);
        const label = item.label;
        setSortParam(label);
        switch (value) {
            case 1:
                addOrReplaceQueryParam('sort', 'nameAsc');
                break;
            case 2:
                addOrReplaceQueryParam('sort', 'nameDesc');
                break;
            case 3:
                addOrReplaceQueryParam('sort', 'createdAtDesc');
                break;
            case 4:
                addOrReplaceQueryParam('sort', 'createdAtAsc');
                break;
        }
    };

    useEffect(() => {
        setCurrentPage(1);
        addOrReplaceQueryParam('page', 1);

        // Create a mapping for asset types
        const assetTypeMapping = {
            1: 1,
            2: 2,
            3: 3,
        };

        // Create a mapping for file types
        const fileTypeMapping = {
            5: ['mp4', 'mov'],
            6: ['mp3', 'wav'],
            7: ['jpg', 'jpeg', 'png', 'svg'],
            8: ['docx', 'doc', 'txt', 'pdf', 'ppt', 'pptx'],
            9: ['xls', 'xlsx', 'csv', 'numbers'],
        };

        // Create a mapping for flags
        const flags = {
            4: 'isShared',
            10: 'isAssetUploader',
            11: 'isSharedToMe',
            12: 'isSharedByMe',
            13: 'isGlobalAsset',
            14: 'isClientUploaded',
        };

        // Check for asset types
        const assetTypes = isResourcePage
            ? [6]
            : Object.keys(assetTypeMapping)
                  .filter(id => filters.some(item => item.id === id))
                  .map(id => assetTypeMapping[id]);
        if (isResourcePage || assetTypes.length > 0) {
            addOrReplaceQueryParam('assetTypes', assetTypes);
        } else {
            removeQueryParam('assetTypes');
        }

        const resourceScopeMapping = {
            15: ResourceScope.Global,
            16: ResourceScope.Agency,
            17: ResourceScope.ClientAdmin,
            18: ResourceScope.Individual,
        };
        const selectedResourceScopes = Object.keys(resourceScopeMapping)
            .filter(id => filters.some(filter => filter.id === id))
            .map(id => resourceScopeMapping[id]);

        const resourceStatusMapping = {
            20: ResourceProgressStatusEnum.NotStarted,
            21: ResourceProgressStatusEnum.InProgress,
            22: ResourceProgressStatusEnum.Completed,
        };
        const selectedResourceStatuses = Object.keys(resourceStatusMapping)
            .filter(id => filters.some(filter => filter.id === id))
            .map(id => resourceStatusMapping[id]);

        if (shouldOmitResourceScopes) {
            removeQueryParam('resourceScopes');
            removeQueryParam('resourceStatuses');
        } else if (isResourcePage && isTrainingConsumptionView) {
            addOrReplaceQueryParam('resourceScopes', resourceScopes);

            if (selectedResourceStatuses.length > 0) {
                addOrReplaceQueryParam(
                    'resourceStatuses',
                    selectedResourceStatuses,
                );
            } else {
                removeQueryParam('resourceStatuses');
            }
        } else if (isResourcePage) {
            addOrReplaceQueryParam(
                'resourceScopes',
                selectedResourceScopes.length > 0
                    ? selectedResourceScopes
                    : resourceScopes,
            );
            removeQueryParam('resourceStatuses');
        } else {
            removeQueryParam('resourceScopes');
            removeQueryParam('resourceStatuses');
        }

        // Check for file types
        const fileTypes = Object.keys(fileTypeMapping)
            .filter(id => filters.some(item => item.id === id))
            .flatMap(id => fileTypeMapping[id]);
        if (fileTypes.length > 0) {
            addOrReplaceQueryParam('fileTypes', fileTypes);
        } else {
            removeQueryParam('fileTypes');
        }

        // Check for flags
        Object.keys(flags).forEach(id => {
            if (id === '13' && isGlobalAssets) {
                addOrReplaceQueryParam(flags[id], true);
            } else if (filters.some(item => item.id === id)) {
                addOrReplaceQueryParam(flags[id], true);
            } else {
                removeQueryParam(flags[id]);
            }
        });
    }, [filters, isGlobalAssets]);

    const handleSvgClick = viewMode => {
        setSelectedViewMode(viewMode);
    };

    const onPageChange = newPage => {
        setCurrentPage(newPage);
        addOrReplaceQueryParam('page', newPage);
    };

    const handleIconClick = event => {
        event.stopPropagation();
        setIsDrawerOpen(true);
    };

    const [isFullscreen, setIsFullscreen] = useState(false);
    const [fullscreenImageUrl, setFullscreenImageUrl] = useState('');
    const [fullscreenMediaType, setFullscreenMediaType] = useState('image');

    const closeFullscreen = () => {
        setIsFullscreen(false);
        setFullscreenImageUrl('');
        setFullscreenMediaType('image');
    };

    const handleViewAsset = asset => {
        const assetCategory = getFileCategory(asset.fileType);
        const canRenderDocumentInBrowser =
            assetCategory === 'document' &&
            canRenderAssetPreviewInBrowser(
                asset.fileType,
                process.env.REACT_APP_DEPLOYED_ENV_NAME,
            );

        if (assetCategory === 'image' || assetCategory === 'video') {
            setFullscreenImageUrl(asset.url);
            setFullscreenMediaType(assetCategory);
            setIsFullscreen(true);
        } else if (canRenderDocumentInBrowser) {
            showModal({
                type: ModalTypes.ASSET_VIEW,
                title: asset.name,
                text: asset.url,
                fileType: asset.fileType,
            });
        } else {
            setActiveCard(assets.indexOf(asset));
            setIsDrawerOpen(true);
        }

        if (isMyAssets) {
            logEvent(
                LogEventType.UserDocumentViewed,
                `Asset ${asset.name} viewed`,
                {
                    subjectDataTypeId: EventDataTypeEnum.Asset,
                    subjectId: asset.id,
                    subjectName: asset.name,
                    contextDataTypeId: EventDataTypeEnum.AssetLibrary,
                    contextId: null,
                    contextName: assetsMenuLabel,
                },
            );
        }
    };

    const handleResourceClick = asset => {
        navigate(`/resource-viewer/${asset.id}`, {
            state: {
                asset,
                returnTo: `${location.pathname}${location.search}`,
            },
        });
    };

    const releaseVideoThumbnails = () => {
        window.dispatchEvent(
            new Event('asset-library:release-video-thumbnails'),
        );
    };

    useEffect(() => {
        setActiveCard(null);
    }, [selectedViewMode, sortParam, queryParams]);

    const removeTag = idToRemove => {
        setFilters(filters.filter(filter => filter.id !== idToRemove));
    };

    const handleClick = index => {
        if (clickTimer.current) {
            clearTimeout(clickTimer.current);
        }

        clickTimer.current = setTimeout(() => {
            setActiveCard(activeCard === index ? null : index);
        }, 300);
    };

    const handleDoubleClick = index => {
        if (activeCard === null) {
            setActiveCard(index);
            setIsDrawerOpen(true);
        } else {
            setActiveCard(null);
        }
    };

    useEffect(() => {
        const handleClickOutside = event => {
            if (isDrawerOpen) return;
            if (!activeCard) return;
            if (
                cardRef.current &&
                !cardRef.current.contains(event.target) &&
                !iconRef?.current.contains(event.target)
            ) {
                setActiveCard(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isDrawerOpen]);

    const handleDeleteAsset = async asset => {
        releaseVideoThumbnails();
        const response = await deleteAsset(asset.id, null);

        if (response.error) {
            Toast.error(response.message);
        } else if (response.status === 200) {
            Toast.success(getText('msg_43778d78002b'));
            setActiveCard(null);
            loadAssets();
        } else {
            Toast.error(getText('msg_ae3d838fb88d'));
        }
    };

    const handleRemoveAsset = async asset => {
        releaseVideoThumbnails();
        const response = await removeAsset(asset.id, null);

        if (response.error) {
            Toast.error(response.message);
        } else if (response.status === 200) {
            Toast.success(getText('msg_8d23ab557865'));
            setActiveCard(null);
            loadAssets();
        } else {
            Toast.error(getText('msg_360dd0d530e2'));
        }
    };

    const handleClickOnDelete = asset => {
        if (isMyAssets && user?.jwt.ind !== asset.individualId) {
            showModal({
                type: ModalTypes.DELETE_CONFIRMATION,
                title: getText('msg_22544c87a3d0'),
                subtitle: getText('msg_7500298cd595'),
                text: getText('msg_0054145b43bf'),
                onYes: () => {
                    handleRemoveAsset(asset);
                },
                onNo: () => {
                    return;
                },
                noButtonText: getText('msg_77dfd2135f4d'),
                yesButtonText: getText('msg_e963907dac5c'),
            });
            logEvent(
                LogEventType.DeleteClicked,
                `Asset library remove clicked`,
            );
        } else {
            showModal({
                type: ModalTypes.DELETE_CONFIRMATION,
                title: getText('msg_eee3584da320'),
                subtitle: getText('msg_874529bd5e14'),
                text: getText('msg_e24943ea4fc9'),
                onYes: () => {
                    handleDeleteAsset(asset);
                },
                onNo: () => {
                    return;
                },
                noButtonText: getText('msg_77dfd2135f4d'),
                yesButtonText: getText('msg_f6fdbe48dc54'),
            });
            logEvent(
                LogEventType.DeleteClicked,
                `Asset library delete clicked`,
            );
        }
    };

    const handleCloseAndRefresh = event => {
        setIsDrawerOpen(event);
        refreshAssets();
        setActiveCard(null);
    };

    const refreshAssets = async () => {
        try {
            setIsLoading(true);
            await new Promise(resolve => setTimeout(resolve, 1000));
            const resourceQuery = buildResourceQuery(queryParams);
            const fetchedAssets = await fetchAssets(
                resourceQuery,
                null,
                isMyAssets,
            );
            setAssets(fetchedAssets.viewModels);
            setTotalPages(fetchedAssets.pageTotal);
        } catch (error) {
            console.error('Failed to fetch assets', error);
            Toast.error(getText('msg_cade149a85b2'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleAddAssetClick = () => {
        let relUrl = isResourcePage
            ? '/resources/new-resource'
            : `/assets/new-asset`;
        if (isMyAssets) {
            relUrl = `/my-assets/new-asset`;
        } else if (isGlobalAssets) {
            relUrl = `/global-assets/new-asset`;
        }

        navigate(relUrl);
    };

    return (
        <div className="page-content">
            <div className={` ${!isMobile && 'card'}`}>
                <div className="card-body px-0 py-4">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <div className="float-start">
                            <p className="page-title card-title ms-4 me-4">
                                {isResourcePage
                                    ? isAdminResourceView ||
                                      isMultiTenantTrainingView
                                        ? getText('msg_b6fe7f5e7917')
                                        : isIndividualResourceView
                                          ? getText('msg_eeeef9b2e5fa')
                                          : getText('msg_dc144320ea69')
                                    : isGlobalAssets
                                      ? getText('msg_501c99a12432')
                                      : isMyAssets
                                        ? assetsMenuLabel
                                        : getText('msg_20e338624cee')}
                            </p>
                            <p
                                className={`ing-assets-subtitle ms-4 me-4 mt-2 ${styles['height-30']}`}
                            >
                                {getPageSubtitle()}
                            </p>
                        </div>
                        <div className="float-end d-flex align-items-center">
                            {!isMobile && (
                                <span
                                    type="button"
                                    onClick={() => {
                                        setFilters([]);
                                    }}
                                    className={classNames(
                                        'ing-text-buttons mx-3',
                                        {
                                            [styles.tabPanelHidden]:
                                                filters.length < 1,
                                        },
                                    )}
                                >
                                    <i className="bi bi-arrow-counterclockwise me-2" />
                                    {getText('msg_381c4bf1d409')}
                                </span>
                            )}
                            {!isAssetLibraryReadOnly &&
                                !isResourceLibraryReadOnly &&
                                !isTrainingConsumptionView &&
                                (!isMyAssets || allowAssetUpload) &&
                                (isMobile ? (
                                    <span
                                        onClick={handleAddAssetClick}
                                        type="button"
                                        className="ms-3 me-4 ing-mobile-button button-base"
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="28"
                                            height="28"
                                            viewBox="0 0 28 28"
                                            fill="none"
                                        >
                                            <path
                                                d="M13.9994 0.285721C14.9462 0.285721 15.7137 1.05323 15.7137 2.00001V12.2857H25.9994C26.9462 12.2857 27.7137 13.0532 27.7137 14C27.7137 14.9468 26.9462 15.7143 25.9994 15.7143H15.7137V26C15.7137 26.9468 14.9462 27.7143 13.9994 27.7143C13.0527 27.7143 12.2852 26.9468 12.2852 26V15.7143H1.99944C1.05267 15.7143 0.285156 14.9468 0.285156 14C0.285156 13.0532 1.05267 12.2857 1.99944 12.2857H12.2852V2.00001C12.2852 1.05323 13.0527 0.285721 13.9994 0.285721Z"
                                                fill="white"
                                            />
                                        </svg>
                                    </span>
                                ) : (
                                    <span
                                        onClick={handleAddAssetClick}
                                        type="button"
                                        className="ms-3 me-4 button-base ing-button-primary-left align-middle"
                                    >
                                        <i className="bi bi-plus-lg me-1" />
                                        {getText('msg_61cc55aa0453')}
                                    </span>
                                ))}
                        </div>
                    </div>
                    {/* {!isMobile && (
                        <hr className={styles['hr-divider']} />
                    )} */}

                    {isMobile ? (
                        <div
                            className={`d-flex align-items-baseline w-100 ${styles.mobileSearchContainer}`}
                        >
                            <div
                                className={`${styles.searchWrapper} ${styles.mobileSearchWrapper}`}
                            >
                                <input
                                    type="text"
                                    className={`${styles.searchInput} ${styles.mobileSearchInput}`}
                                    placeholder={getText('msg_b18cdf8f03e9')}
                                    value={searchTerm}
                                    onChange={handleSearch}
                                />
                                <span className={styles.mobileSearchIcon}>
                                    <i className="bi bi-search"></i>
                                </span>
                            </div>
                            <div
                                className={`d-flex flex-direction-column ${styles.mobileSortWrapper}`}
                            >
                                <Dropdown
                                    defaultValue={sortParam}
                                    onChange={option => {
                                        handleChangeSortBy(option);
                                    }}
                                    options={sortOptions}
                                />
                            </div>
                            <div
                                className={`d-flex align-items-baseline w-100 justify-content-space-between ${styles['margin-top-minus-45']}`}
                            >
                                <div className={styles.mobileFilterWrapper}>
                                    <SearchableDropdown
                                        options={getOptions()}
                                        optionIDField="id"
                                        savedOptions={filters}
                                        savedOptionIDField="id"
                                        updateOptions={options =>
                                            setFilters(options)
                                        }
                                        disabled={false}
                                        buttonMode={true}
                                        buttonLabel={getText(
                                            'msg_d7decf1aa22b',
                                        )}
                                        buttonIcon={'bi bi-filter-circle'}
                                        showTags={false}
                                        className={styles['width-auto']}
                                    />
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div
                            className={`d-flex align-items-baseline w-100 ${styles['margin-left-26']}`}
                        >
                            <div className={styles.searchWrapper}>
                                <input
                                    type="text"
                                    className={styles.searchInput}
                                    placeholder={getText('msg_b18cdf8f03e9')}
                                    value={searchTerm}
                                    onChange={handleSearch}
                                />
                                <span className={styles.searchIcon}>
                                    <i className="bi bi-search"></i>
                                </span>
                            </div>
                            <div
                                className={`${styles['width-280']} ${styles['margin-left-28']}`}
                            >
                                <SearchableDropdown
                                    options={getOptions()}
                                    optionIDField="id"
                                    savedOptions={filters}
                                    savedOptionIDField="id"
                                    updateOptions={options =>
                                        setFilters(options)
                                    }
                                    disabled={false}
                                    buttonMode={true}
                                    buttonLabel={getText('msg_d7decf1aa22b')}
                                    buttonIcon={'bi bi-filter-circle'}
                                    showTags={false}
                                />
                            </div>
                        </div>
                    )}
                    <div
                        className={`d-flex align-items-baseline w-100 ${styles.filterContainer}`}
                    >
                        {filters.map(filter => (
                            <div className={styles.filterTag}>
                                <i
                                    className="bi bi-x"
                                    onClick={() => removeTag(filter.id)}
                                />
                                <span>{filter.name}</span>
                            </div>
                        ))}
                    </div>

                    {!isMobile && (
                        <div className="d-flex align-items-start w-100">
                            <div
                                className={`d-flex flex-direction-column ${styles.desktopSortWrapper}`}
                            >
                                <span className={styles.sortLabel}>
                                    {getText('msg_311158ca7344')}
                                </span>
                                <div className={styles['height-40']}>
                                    <Dropdown
                                        defaultValue={sortParam}
                                        onChange={option => {
                                            handleChangeSortBy(option);
                                        }}
                                        options={sortOptions}
                                    />
                                </div>
                            </div>
                            <div
                                className={`d-flex align-items-center ${styles.viewModeToggleGroup}`}
                            >
                                <div
                                    title={getText('msg_602bf12902a7')}
                                    onClick={() => handleSvgClick('grid')}
                                    className={`${styles.viewModeOption} ${styles.viewModeOptionSpaced}`}
                                >
                                    <div
                                        className={classNames(
                                            selectedViewMode === 'grid'
                                                ? styles.activeViewMode
                                                : styles.inactiveViewMode,
                                            styles.viewModeIconGrid,
                                        )}
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="19"
                                            height="19"
                                            fill={
                                                selectedViewMode === 'grid'
                                                    ? 'var(--iNGAGED-AccessoryForeground-Color1)'
                                                    : 'var(--iNGAGED-Neutral-Color)'
                                            }
                                            viewBox="0 0 16 16"
                                        >
                                            <path d="M1 2.5A1.5 1.5 0 0 1 2.5 1h3A1.5 1.5 0 0 1 7 2.5v3A1.5 1.5 0 0 1 5.5 7h-3A1.5 1.5 0 0 1 1 5.5zM2.5 2a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5zm6.5.5A1.5 1.5 0 0 1 10.5 1h3A1.5 1.5 0 0 1 15 2.5v3A1.5 1.5 0 0 1 13.5 7h-3A1.5 1.5 0 0 1 9 5.5zm1.5-.5a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5zM1 10.5A1.5 1.5 0 0 1 2.5 9h3A1.5 1.5 0 0 1 7 10.5v3A1.5 1.5 0 0 1 5.5 15h-3A1.5 1.5 0 0 1 1 13.5zm1.5-.5a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5zm6.5.5A1.5 1.5 0 0 1 10.5 9h3a1.5 1.5 0 0 1 1.5 1.5v3a1.5 1.5 0 0 1-1.5 1.5h-3A1.5 1.5 0 0 1 9 13.5zm1.5-.5a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5z" />
                                        </svg>
                                    </div>
                                    <span className={styles.viewModeLabel}>
                                        {getText('msg_602bf12902a7')}
                                    </span>
                                </div>
                                <div
                                    title={getText('msg_fb1bde1b6c26')}
                                    onClick={() => handleSvgClick('list')}
                                    className={styles.viewModeOption}
                                >
                                    <div
                                        className={classNames(
                                            selectedViewMode === 'list'
                                                ? styles.activeViewMode
                                                : styles.inactiveViewMode,
                                            styles.viewModeIconList,
                                        )}
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="32"
                                            height="32"
                                            viewBox="0 0 32 32"
                                            fill="none"
                                        >
                                            <circle
                                                cx="16"
                                                cy="16"
                                                r="15.5"
                                                fill={
                                                    selectedViewMode === 'list'
                                                        ? '1366FF'
                                                        : 'white'
                                                }
                                            />
                                            <line
                                                x1="9.6"
                                                y1="15.5"
                                                x2="22.43"
                                                y2="15.5"
                                                stroke={
                                                    selectedViewMode === 'list'
                                                        ? 'var(--iNGAGED-AccessoryForeground-Color1)'
                                                        : 'var(--iNGAGED-Neutral-Color)'
                                                }
                                            />
                                            <line
                                                x1="9.6"
                                                y1="20.3"
                                                x2="22.43"
                                                y2="20.3"
                                                stroke={
                                                    selectedViewMode === 'list'
                                                        ? 'var(--iNGAGED-AccessoryForeground-Color1)'
                                                        : 'var(--iNGAGED-Neutral-Color)'
                                                }
                                            />
                                            <line
                                                x1="9.6"
                                                y1="10.7"
                                                x2="22.43"
                                                y2="10.7"
                                                stroke={
                                                    selectedViewMode === 'list'
                                                        ? 'var(--iNGAGED-AccessoryForeground-Color1)'
                                                        : 'var(--iNGAGED-Neutral-Color)'
                                                }
                                            />
                                        </svg>
                                    </div>
                                    <span className={styles.viewModeLabel}>
                                        {getText('msg_fb1bde1b6c26')}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}
                    {isLoading ? (
                        <Spinner width={50} className={styles.loadingSpinner} />
                    ) : (
                        <>
                            {selectedViewMode === 'grid' && (
                                <AssetGridView
                                    assets={assets}
                                    handleClick={handleClick}
                                    handleDoubleClick={handleDoubleClick}
                                    handleClickOnDelete={handleClickOnDelete}
                                    activeCard={activeCard}
                                    onIconClick={handleIconClick}
                                    onViewClick={handleViewAsset}
                                    setActiveCard={setActiveCard}
                                    isMyAssets={isMyAssets}
                                    isGlobalAssets={isGlobalAssets}
                                    isAssetLibraryReadOnly={
                                        isAssetLibraryReadOnly
                                    }
                                    isClientResourceView={
                                        isTrainingConsumptionView
                                    }
                                    hideGlobalAssetIcon={isResourcePage}
                                    onResourceClick={handleResourceClick}
                                />
                            )}
                            {selectedViewMode === 'list' && (
                                <AssetListView
                                    assets={assets}
                                    handleClick={handleDoubleClick}
                                    isClientResourceView={
                                        isTrainingConsumptionView
                                    }
                                    onResourceClick={handleResourceClick}
                                />
                            )}
                            {assets.length < 1 && (
                                <NotFoundBox
                                    backgroundColor="#f0f4ff"
                                    title={
                                        'search' in queryParams ||
                                        'assetTypes' in queryParams ||
                                        'fileTypes' in queryParams
                                            ? getText('msg_aca14ec90fb5')
                                            : getText('msg_e1f2a3b4c5d6')
                                    }
                                    description={
                                        'search' in queryParams ||
                                        'assetTypes' in queryParams ||
                                        'fileTypes' in queryParams
                                            ? getText('msg_a2b3c4d5e6f7')
                                            : getText('msg_f1a2b3c4d5e6')
                                    }
                                />
                            )}
                            {isMobile ? (
                                <></>
                            ) : (
                                <PaginationComponent
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    onPageChange={onPageChange}
                                />
                            )}
                        </>
                    )}
                </div>
            </div>
            {isDrawerOpen && (
                <AssetDrawer
                    onClose={e => {
                        setActiveCard(null);
                        setIsDrawerOpen(e);
                    }}
                    onSaveOrDelete={e => {
                        handleCloseAndRefresh(e);
                    }}
                    assetId={assets[activeCard].id}
                    isMyAssets={isMyAssets}
                    isGlobalAssets={isGlobalAssets}
                    eventContextDataTypeId={EventDataTypeEnum.AssetLibrary}
                    eventContextId={null}
                    eventContextName={assetsMenuLabel}
                    isResourceLibrary={isResourcePage}
                    isResourceLibraryReadOnly={isResourceLibraryReadOnly}
                />
            )}
            <FullscreenDisplay
                isFullscreen={isFullscreen}
                fullscreenImageUrl={fullscreenImageUrl}
                mediaType={fullscreenMediaType}
                closeFullscreen={closeFullscreen}
            />
        </div>
    );
};

export default AssetLibrary;
